import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Billing,
  CreateOrderDTO,
  Order,
  OrderResponse,
  OrderStatus,
  Payment,
  PendingCourseOrder,
} from './order.model';
import { PublicTenantPaymentConfig } from '../../../models/invoice.model';
import { environment } from '../../../../../environments/environment';
import { catchError, map, of } from 'rxjs';

@Injectable()
export class OrderService {
  private http = inject(HttpClient);


  private baseUrl = environment.host;

  create(order: CreateOrderDTO | Order) {
    return this.http.put<Order>(this.baseUrl + '/api/v1/order', order);
  }

  getById(id: string) {
    return this.http.get<Order>(this.baseUrl + '/api/v1/order/' + id);
  }

  getOrderPaymentConfig(orderId: string) {
    return this.http.get<PublicTenantPaymentConfig>(
      this.baseUrl + '/api/v1/order/' + orderId + '/payment-config',
    );
  }

  getAll(page = 0, limit = 10, status?: string, mine?: boolean) {
    let url = this.baseUrl + '/api/v1/order?page=' + page + '&limit=' + limit;
    if (status) url += '&status=' + encodeURIComponent(status);
    if (mine) url += '&mine=true';
    return this.http.get<OrderResponse>(url);
  }

  getPendingCourseOrders() {
    return this.getAll(1, 50, undefined, true).pipe(
      map((res) => {
        const statusMap = new Map<string, PendingCourseOrder>();
        const now = new Date();

        for (const order of res?.docs || []) {
          const orderId = order.orderId || order._id;
          let status: 'AWAITING_APPROVAL' | 'PENDING' | 'OVERDUE' | null = null;
          const currentInst = order.installments?.find(
            (i) => i.status === 'PENDING' || i.status === 'OVERDUE'
          );

          const graceEnd = currentInst
            ? new Date(new Date(currentInst.dueDate).getTime() + 5 * 86400000)
            : undefined;

          if (order.status === OrderStatus.OVERDUE) {
            status = 'OVERDUE';
          } else if (order.status === OrderStatus.AWAITING_APPROVAL) {
            status = 'AWAITING_APPROVAL';
          } else if (order.status === OrderStatus.PENDING) {
            status = 'PENDING';
          } else if (order.status === OrderStatus.PARTIALLY_PAID && currentInst && graceEnd) {
            status = now > graceEnd ? 'OVERDUE' : now >= new Date(currentInst.dueDate) ? 'PENDING' : null;
          }

          if (!status) continue;

          for (const course of order.items || []) {
            if (course?._id) {
              statusMap.set(course._id, {
                status,
                orderId,
                course,
                dueDate: currentInst?.dueDate,
                gracePeriodEnd: graceEnd,
                installmentNumber: currentInst?.installmentNumber,
                amount: currentInst?.amount,
              });
            }
          }
        }
        return statusMap;
      }),
      catchError(() => of(new Map<string, PendingCourseOrder>()))
    );
  }

  processPayment(orderId: string, payment: Payment) {
    return this.http.put<Order>(
      this.baseUrl + '/api/v1/order/' + orderId + '/payment',
      payment
    );
  }

  initiatePayherePayment(
    orderId: string,
    billingInformation: Billing,
    returnUrl: string,
    cancelUrl: string
  ) {
    return this.http.post<Record<string, string>>(
      this.baseUrl + '/api/v1/order/' + orderId + '/payhere/initiate',
      { billingInformation, returnUrl, cancelUrl }
    );
  }

  completeOrder(orderId: string) {
    return this.http.put<Order>(
      this.baseUrl + '/api/v1/order/' + orderId + '/complete',
      {}
    );
  }
}
